/**
 * Defensive block: the back line, the midfield line, and the gap between them
 * (founder ask, 2026-08-27).
 *
 * "Did they play through us, around us, or over us" is the question, and the
 * gap between the two lines is where it gets answered. Computed client-side
 * from the tracking positions the page already holds — there is an
 * offside_lines parquet, but it feeds video telestration, which needs
 * homography this game does not have.
 *
 * WHICH WAY IS THE TEAM DEFENDING? Inferred per frame rather than configured:
 * the goalkeeper is the outfield-most player in x, and the sign of their
 * position says which goal they are defending. That survives half-time
 * without anyone updating a setting, and it degrades honestly — a frame with
 * too few tracked players returns null instead of a guess.
 */
import type { TrackingFrame } from './trackingLoader'

/** Pitch half-length in centimetres; tracking x is cm from the centre spot. */
const HALF_LEN_CM = 105 * 100 / 2

export interface Block {
  team: number
  /** -1 when the team defends the negative-x goal, +1 the positive-x goal. */
  defendingSign: -1 | 1
  /** Centimetres, on the tracking axis. */
  keeperX: number
  defensiveLineX: number
  midfieldLineX: number
  /** Depth of the band in metres — the number an analyst actually quotes. */
  gapMetres: number
  /** Opponents standing between the two lines. */
  opponentsBetween: number
}

/** Players of one team, outfield only, that have a usable position. */
function outfield(frame: TrackingFrame, team: number) {
  return (frame.players ?? []).filter(
    p => p.team === team && p.jersey != null && Number.isFinite(p.x) && Number.isFinite(p.y))
}

/**
 * The block for one team on one frame, or null when the frame cannot support
 * the claim (too few players tracked).
 *
 * `backCount`/`midCount` are how many players form each line — four and four
 * is the common shape, and averaging a band is far steadier frame to frame
 * than taking a single extreme player, who may be a full-back stepping out.
 */
export function computeBlock(
  frame: TrackingFrame,
  team: number,
  { backCount = 4, midCount = 4 }: { backCount?: number; midCount?: number } = {},
): Block | null {
  const players = outfield(frame, team)
  // Keeper + two lines is the minimum that means anything.
  if (players.length < backCount + midCount + 1) return null

  // The keeper is the deepest player; which end they sit at gives the
  // defending direction without any configuration.
  const byX = [...players].sort((a, b) => a.x - b.x)
  const lowest = byX[0]
  const highest = byX[byX.length - 1]
  // The length check above proves both ends exist; the checker cannot see
  // through it, and a block line drawn from a missing player is not worth an
  // assertion to save two lines.
  if (lowest === undefined || highest === undefined) return null
  const defendingSign: -1 | 1 =
    Math.abs(lowest.x) > Math.abs(highest.x) ? -1 : 1
  const keeper = defendingSign === -1 ? lowest : highest

  // Rank by depth: deepest first, from the defended goal outward.
  const ranked = players
    .filter(p => p !== keeper)
    .sort((a, b) => defendingSign === -1 ? a.x - b.x : b.x - a.x)
  if (ranked.length < backCount + midCount) return null

  const mean = (xs: { x: number }[]) => xs.reduce((s, p) => s + p.x, 0) / xs.length
  const defensiveLineX = mean(ranked.slice(0, backCount))
  const midfieldLineX = mean(ranked.slice(backCount, backCount + midCount))

  const lo = Math.min(defensiveLineX, midfieldLineX)
  const hi = Math.max(defensiveLineX, midfieldLineX)
  const opponentsBetween = outfield(frame, team === 0 ? 1 : 0)
    .filter(p => p.x >= lo && p.x <= hi).length

  return {
    team,
    defendingSign,
    keeperX: keeper.x,
    defensiveLineX,
    midfieldLineX,
    gapMetres: (hi - lo) / 100,
    opponentsBetween,
  }
}

/** Clamp a tracking x to the pitch, for drawing. */
export function clampToPitch(xCm: number): number {
  return xCm < -HALF_LEN_CM ? -HALF_LEN_CM : xCm > HALF_LEN_CM ? HALF_LEN_CM : xCm
}

interface BlockOptions {
  getCanvasState: () => {
    ctx: CanvasRenderingContext2D | null
    frames: TrackingFrame[]
    currentFrameIndex: number
  }
  convertToCanvasCoords: (x: number, y: number) => { x: number; y: number }
  getTeamColors: () => Record<number, string>
  /** Which side's block to draw. */
  getTeam: () => number
}

export class BlockLinesManager {
  enabled = false
  config = { backCount: 4, midCount: 4, showLabel: true }

  private opts: BlockOptions

  constructor(opts: BlockOptions) {
    this.opts = opts
  }

  draw(): void {
    if (!this.enabled) return
    const { ctx, frames, currentFrameIndex } = this.opts.getCanvasState()
    if (!ctx || !frames.length) return
    const frame = frames[Math.min(currentFrameIndex, frames.length - 1)]
    if (!frame) return

    const team = this.opts.getTeam()
    const block = computeBlock(frame, team, this.config)
    if (!block) return

    const color = this.opts.getTeamColors()[team] || '#999'
    const top = this.opts.convertToCanvasCoords(0, 3400)
    const bottom = this.opts.convertToCanvasCoords(0, -3400)
    const dx = this.opts.convertToCanvasCoords(clampToPitch(block.defensiveLineX), 0).x
    const mx = this.opts.convertToCanvasCoords(clampToPitch(block.midfieldLineX), 0).x

    ctx.save()
    // The band first, so the lines read as its edges.
    ctx.globalAlpha = 0.13
    ctx.fillStyle = color
    ctx.fillRect(Math.min(dx, mx), top.y, Math.abs(mx - dx), bottom.y - top.y)

    ctx.globalAlpha = 0.9
    ctx.strokeStyle = color
    ctx.lineWidth = 2
    for (const x of [dx, mx]) {
      ctx.beginPath()
      ctx.moveTo(x, top.y)
      ctx.lineTo(x, bottom.y)
      ctx.stroke()
    }

    if (this.config.showLabel) {
      // The two numbers an analyst would say out loud.
      const label = `${block.gapMetres.toFixed(0)}m between the lines · `
        + `${block.opponentsBetween} in the gap`
      ctx.globalAlpha = 1
      ctx.font = '600 12px -apple-system, sans-serif'
      const w = ctx.measureText(label).width + 12
      const cx = (dx + mx) / 2 - w / 2
      ctx.fillStyle = 'rgba(10,13,18,0.8)'
      ctx.fillRect(cx, top.y + 6, w, 20)
      ctx.fillStyle = '#dde3ec'
      ctx.fillText(label, cx + 6, top.y + 20)
    }
    ctx.restore()
  }
}
